import {
  add,
  differenceInCalendarDays,
  eachDayOfInterval,
  endOfMonth,
  format,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import { FilteredKiSyokenComparisonSection } from '../../../domain/kiSyoken/types';

import { makeKiSyokenComparisonDateFromSelectedComparativeSection } from './kiSyokenComparativeSectionUtil';

/**
 * 日付範囲からymdListを生成するユーティリティ関数
 * @param startDate - 開始日 (YYYY-MM-DD形式)
 * @param endDate - 終了日 (YYYY-MM-DD形式)
 * @returns 日付文字列の配列 (YYYY-MM-DD形式)
 */
export const createYmdList = (startDate: string, endDate: string): string[] => {
  return eachDayOfInterval({
    start: new Date(startDate),
    end: new Date(endDate),
  }).map((d) => format(d, 'yyyy-MM-dd'));
};

/**
 * 比較期間の再計算が必要な比較区分かどうかを判定する
 * @param section - 比較区分
 * @returns 比較期間の再計算が必要な比較区分かどうか
 */
export const isComparisonPeriodRecalculationNeeded = (
  section: FilteredKiSyokenComparisonSection
): boolean => {
  // カスタムは主期間と同一なので再計算不要
  // 過去xx日も、主期間の選択によらず常に直近xx日間を対象とするため、再計算不要
  if (section === 'カスタム' || section === '過去xx日') {
    return false;
  }

  // その他の比較区分は全て再計算が必要
  // （日付変更時に基準日が変わるため）
  return true;
};

/**
 * 期間単位「月」の場合の比較期間を計算する（useDate用）
 * 主期間の長さに合わせた比較期間を返す
 * @param comparisonSection - 比較区分
 * @param mainStartDate - 主期間の開始日
 * @param mainEndDate - 主期間の終了日
 * @param excludeToday - 今日を除外するかどうか
 * @returns 比較期間
 */
export const calculateMonthComparisonPeriodInUseDate = (
  comparisonSection: FilteredKiSyokenComparisonSection,
  mainStartDate: Date,
  mainEndDate: Date,
  excludeToday: boolean
): string[] => {
  const now = new Date();

  // 主期間の日数を算出
  const mainDays = eachDayOfInterval({
    start: mainStartDate,
    end: mainEndDate,
  });
  const actualDays = mainDays.length;

  // excludeToday: 主期間に今日が含まれる場合のみ比較期間の日数を1日減らす（ただし期間が1日の場合は減らさない）
  const todayYmd = format(now, 'yyyy-MM-dd');
  const includesTodayInMain = mainDays
    .map((d) => format(d, 'yyyy-MM-dd'))
    .includes(todayYmd);
  const targetDays =
    excludeToday && includesTodayInMain && actualDays > 1
      ? actualDays - 1
      : actualDays;

  switch (comparisonSection) {
    case '前月': {
      const lastMonth = add(mainStartDate, { months: -1 });
      const startDate = startOfMonth(lastMonth);
      const monthEnd = endOfMonth(lastMonth);
      const endDateCandidate = add(startDate, {
        days: Math.max(0, targetDays - 1),
      });
      const endDate = endDateCandidate > monthEnd ? monthEnd : endDateCandidate;
      return eachDayOfInterval({ start: startDate, end: endDate }).map((date) =>
        format(date, 'yyyy-MM-dd')
      );
    }
    case '前年同月': {
      const lastYear = add(mainStartDate, { years: -1 });
      const startDate = startOfMonth(lastYear);
      const monthEnd = endOfMonth(lastYear);
      const endDateCandidate = add(startDate, {
        days: Math.max(0, targetDays - 1),
      });
      const endDate = endDateCandidate > monthEnd ? monthEnd : endDateCandidate;
      return eachDayOfInterval({ start: startDate, end: endDate }).map((date) =>
        format(date, 'yyyy-MM-dd')
      );
    }
    default: {
      const comparisonResult =
        makeKiSyokenComparisonDateFromSelectedComparativeSection(
          comparisonSection,
          mainStartDate,
          now,
          excludeToday
        );
      return comparisonResult.ymdComparisonList.map((date) =>
        format(date, 'yyyy-MM-dd')
      );
    }
  }
};

/**
 * 比較区分に応じて比較期間を計算する内部関数
 * @param section - 比較区分
 * @param currentStartDate - 主期間の開始日
 * @param currentEndDate - 主期間の終了日
 * @param excludeToday - 今日を除外するかどうか
 * @returns 比較期間
 */
export const calculateComparisonPeriod = (
  section: FilteredKiSyokenComparisonSection,
  currentStartDate: string,
  currentEndDate: string,
  excludeToday: boolean
): {
  startComparisonDate: string;
  endComparisonDate: string;
  ymdComparisonList?: string[];
} => {
  // 平日/土日祝比較
  if (section === '平日/土日祝比較') {
    const ymdComparisonList = eachDayOfInterval({
      start: new Date(currentStartDate),
      end: new Date(currentEndDate),
    }).map((d) => format(d, 'yyyy-MM-dd'));

    return {
      startComparisonDate: currentStartDate,
      endComparisonDate: currentEndDate,
      ymdComparisonList,
    };
  }

  // カスタム
  if (section === 'カスタム') {
    return {
      startComparisonDate: currentStartDate,
      endComparisonDate: currentEndDate,
    };
  }

  // 過去XX日の場合は期間全体をシフト
  if (section === '過去xx日') {
    const startDate = new Date(currentStartDate);
    const endDate = new Date(currentEndDate);

    // 期間の開始(currentStartDate)と終了(currentEndDate)から差分日数を計算
    const shiftDays =
      differenceInCalendarDays(
        new Date(currentEndDate),
        new Date(currentStartDate)
      ) + 1;
    // 比較期間は計算した差分日数だけ過去にシフトする
    const startComparisonDate = add(startDate, { days: -shiftDays });
    const endComparisonDate = add(endDate, { days: -shiftDays });

    const ymdComparisonList = eachDayOfInterval({
      start: startComparisonDate,
      end: endComparisonDate,
    }).map((d) => format(d, 'yyyy-MM-dd'));

    return {
      startComparisonDate: format(startComparisonDate, 'yyyy-MM-dd'),
      endComparisonDate: format(endComparisonDate, 'yyyy-MM-dd'),
      ymdComparisonList,
    };
  }

  // 月の比較区分の特別処理
  if (section === '前月' || section === '前年同月') {
    const comparisonYmdList = calculateMonthComparisonPeriodInUseDate(
      section,
      new Date(currentStartDate),
      new Date(currentEndDate),
      excludeToday
    );
    return {
      startComparisonDate: comparisonYmdList[0],
      endComparisonDate: comparisonYmdList[comparisonYmdList.length - 1],
      ymdComparisonList: comparisonYmdList,
    };
  }

  // 週の比較区分の特別処理：主期間の日数に合わせる
  if (section === '前週') {
    const startDate = new Date(currentStartDate);
    const endDate = new Date(currentEndDate);

    // 主期間の実際の日数を計算
    const mainPeriodDays = eachDayOfInterval({
      start: startDate,
      end: endDate,
    });
    const actualDays = mainPeriodDays.length;

    // excludeToday: 主期間に今日が含まれる場合のみ比較期間の日数を1日減らす（ただし期間が1日の場合は減らさない）
    const todayYmd = format(new Date(), 'yyyy-MM-dd');
    const includesTodayInMain = mainPeriodDays
      .map((d) => format(d, 'yyyy-MM-dd'))
      .includes(todayYmd);
    const adjustedDays =
      excludeToday && includesTodayInMain && actualDays > 1
        ? actualDays - 1
        : actualDays;

    // 主期間の属する週の前週（月曜起点）を基準にする
    const mainWeekStart = startOfWeek(startDate, { weekStartsOn: 1 });
    const prevWeekStart = add(mainWeekStart, { weeks: -1 });
    const prevWeekEnd = add(prevWeekStart, { days: adjustedDays - 1 });

    let ymdComparisonList = eachDayOfInterval({
      start: prevWeekStart,
      end: prevWeekEnd,
    }).map((d) => format(d, 'yyyy-MM-dd'));

    if (ymdComparisonList.length > adjustedDays) {
      ymdComparisonList = ymdComparisonList.slice(0, adjustedDays);
    }

    return {
      startComparisonDate: ymdComparisonList[0],
      endComparisonDate: ymdComparisonList[ymdComparisonList.length - 1],
      ymdComparisonList,
    };
  }

  // その他の比較区分: 機種別商圏専用関数を使用
  const baseDate = new Date(currentStartDate);
  const now = new Date();

  const result = makeKiSyokenComparisonDateFromSelectedComparativeSection(
    section,
    baseDate,
    now,
    excludeToday
  );

  const ymdComparisonList = result.ymdComparisonList.map((d) =>
    format(d, 'yyyy-MM-dd')
  );

  if (ymdComparisonList.length === 0) {
    // フォールバック: 主期間と同一にする
    return {
      startComparisonDate: currentStartDate,
      endComparisonDate: currentEndDate,
    };
  }

  return {
    startComparisonDate: ymdComparisonList[0],
    endComparisonDate: ymdComparisonList[ymdComparisonList.length - 1],
    ymdComparisonList,
  };
};
